import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../../environments/environment';
import { AcknowledgeDialogComponent } from '../acknowledge-dialog/acknowledge-dialog.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { AddLicenseDialogComponent } from './add-license-dialog.component';

interface LicenseItem {
  licenseId: number;
  licenseKey: string;
  purchaseEmail: string;
  productId: string;
  userId?: string;
  period: number;
  periodType: 'D'|'M'|'Y';
  purchaseDate?: string;
  activationDate?: string;
  deactivationDate?: string;
  activated: boolean;
}

@Component({
  selector: 'app-license-admin',
  templateUrl: './license-admin.component.html',
  styleUrls: ['./license-admin.component.scss']
})
export class LicenseAdminComponent implements OnInit {
  // list state
  keywords: string = '';
  page = 0; size = 20;
  total = 0; items: LicenseItem[] = [];
  selected?: LicenseItem;
  isAdmin = true; // header already controls visibility; keep for template toggles if needed
  deactivating = false;

  // details state
  editMode = false;

  constructor(private http: HttpClient, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.fetchList();
  }

  fetchList(resetPage = false): void {
    if (resetPage) this.page = 0;
    const url = this.keywords?.trim()
      ? `${environment.apiBaseUrl}/licenses/search`
      : `${environment.apiBaseUrl}/licenses`;
    const params = new HttpParams()
      .set('page', String(this.page))
      .set('size', String(this.size))
      .set('sort', 'licenseId');
    const obs = this.keywords?.trim()
      ? this.http.get<any>(url, { params: params.set('keywords', this.keywords.trim()) })
      : this.http.get<any>(url, { params });
    obs.subscribe({
      next: (pg) => {
        this.items = pg?.content ?? [];
        this.total = pg?.totalElements ?? this.items.length;
      },
      error: (err) => {
        this.dialog.open(AcknowledgeDialogComponent, { data: err?.error?.error || err.message || 'Load failed' });
      }
    });
  }

  selectRow(it: LicenseItem): void { this.selected = it; this.editMode = false; }

  // pagination helpers
  get totalPages(): number { return Math.max(1, Math.ceil(this.total / this.size)); }
  goPrev(): void { if (this.page > 0) { this.page--; this.fetchList(); } }
  goNext(): void { if (this.page + 1 < this.totalPages) { this.page++; this.fetchList(); } }

  // add
  openAddDialog(): void {
    const ref = this.dialog.open(AddLicenseDialogComponent, { width: '480px' });
    ref.afterClosed().subscribe(form => {
      if (!form) return;
      this.http.post(`${environment.apiBaseUrl}/license`, form).subscribe({
        next: () => { this.dialog.open(AcknowledgeDialogComponent, { data: 'Created.' }); this.fetchList(true); },
        error: (err) => { this.dialog.open(AcknowledgeDialogComponent, { data: err?.error?.error || err.message || 'Create failed' }); }
      });
    });
  }

  // delete
  onDelete(): void {
    if (!this.selected) return;
    const ref = this.dialog.open(ConfirmDialogComponent, { width: '360px', data: { title: 'Delete License', message: 'Are you sure to delete this license?' } });
    ref.afterClosed().subscribe(ok => {
      if (!ok) return;
      this.http.delete(`${environment.apiBaseUrl}/license/${this.selected?.licenseId}`).subscribe({
        next: () => { this.dialog.open(AcknowledgeDialogComponent, { data: 'Deleted.' }); this.fetchList(); this.selected = undefined; },
        error: (err) => { this.dialog.open(AcknowledgeDialogComponent, { data: err?.error?.error || err.message || 'Delete failed' }); }
      });
    });
  }

  // save details
  onSave(): void {
    if (!this.selected) return;
    const normalize = (v?: string) => {
      if (!v) return v;
      // if value is yyyy-MM-dd (no time), append T00:00:00 for LocalDateTime parsing
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${v}T00:00:00`;
      return v;
    };
    const body = { ...this.selected } as any;
    body.purchaseDate = normalize(body.purchaseDate);
    body.activationDate = normalize(body.activationDate);
    body.deactivationDate = normalize(body.deactivationDate);
    this.http.put(`${environment.apiBaseUrl}/license/${this.selected.licenseId}`, body).subscribe({
      next: (out) => { this.dialog.open(AcknowledgeDialogComponent, { data: 'Saved.' }); this.editMode = false; this.fetchList(); },
      error: (err) => { this.dialog.open(AcknowledgeDialogComponent, { data: err?.error?.error || err.message || 'Save failed' }); }
    });
  }

  onDeactivateBatch(): void {
    const ref = this.dialog.open(ConfirmDialogComponent, { width: '420px', data: { title: 'Deactivate Licenses', message: 'Are you sure to deactivate expired licenses in batch?' } });
    ref.afterClosed().subscribe(ok => {
      if (!ok) return;
      this.deactivating = true;
      this.http.post(`${environment.apiBaseUrl}/license/deactivate`, {}).subscribe({
        next: (out: any) => {
          const processed = (out && (out.processed ?? '')) as any;
          const msg = (out && (out.message ?? 'ok')) as string;
          const text = `/license/deactivate\n${msg}${processed !== '' ? ` (processed: ${processed})` : ''}`;
          this.dialog.open(AcknowledgeDialogComponent, { data: text });
          this.deactivating = false;
          this.fetchList();
        },
        error: (err) => {
          const body = err?.error?.error || err?.message || 'Deactivate failed';
          const text = `/license/deactivate\n${body}`;
          this.dialog.open(AcknowledgeDialogComponent, { data: text });
          this.deactivating = false;
        }
      });
    });
  }
}


